import React, { useEffect, useMemo, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import {
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { ChevronDown } from 'lib/lucide'
import { DatePicker } from '@material-ui/pickers'
import { MultiSelect } from 'react-multi-select-component'
import moment, { Moment } from 'moment'
import { useTranslation } from 'react-i18next'

import { ImageUploadField, snackbar } from 'components'
import { STRINGS } from 'consts'
import { useCheckFormErrors } from 'hooks'
import { resizeFile, StringUtils } from 'utils'

import { EditorInput } from '../../components'
import { Membership } from '../../Memberships/types'
import { Tag } from '../../Tags/types'
import { Store } from '../../Stores/types'
import { isFreeFixedValidityLocked } from '../constants'
import {
  ApplicationScope,
  ParkingConfig,
  ParkingConfigFormPayload,
  ParkingFormType,
  ParkingType,
  UnlockMode,
} from '../types'
import { useStyles } from './styles'

const PRESENTATION_REQUIRED_FIELDS = [
  'title',
  'shortTitle',
  'description',
  'icon',
  'image',
] as const
const PARKING_FORM_TYPES: ParkingFormType[] = [
  'fixed',
  'free_fixed',
  'unlock',
  'unlock_qr',
  'unlimited',
]
const UNLOCK_MODES: UnlockMode[] = ['single_invoice', 'daily_accumulation']

const TAB_PRESENTACION = 0
const TAB_REGLAS = 1
const TAB_ALCANCE = 2

interface SelectOption {
  label: string
  value: number
}

interface LevelState {
  included: boolean
  hours: string
  minutes: string
}

const inferInitialApplicationScope = (config?: ParkingConfig): ApplicationScope => {
  if (!config || config.parkingType === 'fixed' || config.externalTicket) return ''
  if ((config.stores?.length ?? 0) > 0) return 'brands'
  if ((config.tags?.length ?? 0) > 0) return 'tags'
  return ''
}

const buildInitialStoreSelection = (config?: ParkingConfig): SelectOption[] => {
  if (!config || config.parkingType === 'fixed' || config.externalTicket) return []
  if (inferInitialApplicationScope(config) !== 'brands') return []
  return (config.stores ?? []).map(store => ({ label: store.name, value: store.id }))
}

const buildInitialTagSelection = (config?: ParkingConfig): SelectOption[] => {
  if (!config || config.parkingType === 'fixed') return []
  if (inferInitialApplicationScope(config) !== 'tags') return []
  return (config.tags ?? []).map(tag => ({ label: tag.title, value: tag.id }))
}

const formatStoreLabel = (store: { name: string; secondaryName?: string | null }) => {
  const fantasyName = store.secondaryName?.trim()
  if (!fantasyName) return store.name
  return `${store.name} - ${fantasyName}`
}

const formatStoreChipLabel = (store: { name: string; secondaryName?: string | null }) => {
  const fantasyName = store.secondaryName?.trim()
  return fantasyName || store.name
}

interface ParkingConfigFormProps {
  initialConfig?: ParkingConfig
  isCreate?: boolean
  isLoading?: boolean
  memberships: Membership[]
  tags: Tag[]
  stores: Store[]
  active: boolean
  onSubmit: (payload: ParkingConfigFormPayload) => void
  onValidityChange?: (disabled: boolean) => void
  formId?: string
}

const getPrizeFromConfig = (config?: ParkingConfig) => config?.levels?.[0]?.prize

const splitDurationMinutes = (
  durationMinutes: number | null | undefined,
): { hours: string; minutes: string } => {
  if (durationMinutes == null || durationMinutes <= 0) {
    return { hours: '0', minutes: '0' }
  }
  return {
    hours: String(Math.floor(durationMinutes / 60)),
    minutes: String(durationMinutes % 60),
  }
}

const parseDurationPart = (value: string, max: number): number => {
  if (value.trim() === '') return 0
  const parsed = Number(value)
  if (Number.isNaN(parsed) || parsed < 0) return 0
  return Math.min(max, Math.floor(parsed))
}

const toTotalMinutes = (hours: string, minutes: string): number =>
  parseDurationPart(hours, 23) * 60 + parseDurationPart(minutes, 59)

const parseConfigDate = (value?: string | null): Moment | null => {
  if (!value) return null
  const parsed = moment(String(value).slice(0, 10), 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed : null
}

const toDateOnly = (value: Moment | null): string | null =>
  value && value.isValid() ? value.format('YYYY-MM-DD') : null

const toParkingFormType = (
  config?: Pick<ParkingConfig, 'parkingType' | 'isFree' | 'externalTicket'>,
): ParkingFormType => {
  if (!config) return 'unlock'
  if (config.isFree) return 'free_fixed'
  if (config.externalTicket) return 'unlock_qr'
  return config.parkingType
}

const buildInitialLevels = (
  memberships: Membership[],
  config?: ParkingConfig,
): Record<number, LevelState> => {
  const levelsByMembership = new Map(
    (config?.levels ?? []).map(level => [level.membershipId, level]),
  )

  return memberships.reduce<Record<number, LevelState>>((acc, membership) => {
    const existing = levelsByMembership.get(membership.id)
    const split = splitDurationMinutes(existing?.durationMinutes)
    acc[membership.id] = {
      included: !!existing,
      hours: split.hours,
      minutes: split.minutes,
    }
    return acc
  }, {})
}

const ParkingConfigForm: React.FC<ParkingConfigFormProps> = ({
  initialConfig,
  isCreate = false,
  isLoading = false,
  memberships,
  tags,
  stores,
  active,
  onSubmit,
  onValidityChange,
  formId = 'parking-config-form',
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const prize = getPrizeFromConfig(initialConfig)

  const [activeTab, setActiveTab] = useState(TAB_PRESENTACION)
  const [parkingType, setParkingType] = useState<ParkingType>(
    initialConfig?.parkingType ?? 'unlock',
  )
  const [isFree, setIsFree] = useState(!!initialConfig?.isFree)
  const [validFrom, setValidFrom] = useState<Moment | null>(parseConfigDate(initialConfig?.validFrom))
  const [validTo, setValidTo] = useState<Moment | null>(parseConfigDate(initialConfig?.validTo))
  const [unlockAmount, setUnlockAmount] = useState(
    initialConfig?.unlockAmount != null ? String(initialConfig.unlockAmount) : '',
  )
  const [unlockMode, setUnlockMode] = useState<UnlockMode | ''>(initialConfig?.unlockMode ?? '')
  const [externalTicket, setExternalTicket] = useState(initialConfig?.externalTicket ?? false)
  const [qrSystemId, setQrSystemId] = useState(initialConfig?.qrSystemId ?? '')
  const [qrIssuerId, setQrIssuerId] = useState(initialConfig?.qrIssuerId ?? '')
  const [qrBenefitId, setQrBenefitId] = useState(
    initialConfig?.qrBenefitId != null ? String(initialConfig.qrBenefitId) : '',
  )
  const [applicationScope, setApplicationScope] = useState<ApplicationScope>(
    inferInitialApplicationScope(initialConfig),
  )
  const [title, setTitle] = useState(prize?.title ?? '')
  const [shortTitle, setShortTitle] = useState(prize?.shortTitle ?? '')
  const [description, setDescription] = useState(prize?.description ?? '')
  const [additionalText, setAdditionalText] = useState(prize?.additionalText ?? '')
  const [icon, setIcon] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState(prize?.iconUrl ?? '')
  const [iconPreviewBroken, setIconPreviewBroken] = useState(false)
  const [image, setImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState(prize?.imageUrl ?? '')
  const [imagePreviewBroken, setImagePreviewBroken] = useState(false)
  const [selectedTags, setSelectedTags] = useState<SelectOption[]>(() =>
    buildInitialTagSelection(initialConfig),
  )
  const [selectedStores, setSelectedStores] = useState<SelectOption[]>(() =>
    buildInitialStoreSelection(initialConfig),
  )
  const [levelsState, setLevelsState] = useState<Record<number, LevelState>>({})
  const [levelsTouched, setLevelsTouched] = useState(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const selectedTagIds = useMemo(() => selectedTags.map(tag => tag.value), [selectedTags])
  const selectedStoreIds = useMemo(() => selectedStores.map(store => store.value), [selectedStores])

  const tagCatalogOptions = useMemo(
    () =>
      tags.filter(tag => tag.active !== false).map(tag => ({ label: tag.title, value: tag.id })),
    [tags],
  )

  const storeCatalogOptions = useMemo(
    () =>
      stores
        .filter(store => store.active !== false)
        .map(store => ({ label: formatStoreLabel(store), value: store.id })),
    [stores],
  )

  const brandOptions = storeCatalogOptions
  const multiSelectStrings = useMemo(
    () => ({ search: t('parking:upload.search') as string }),
    [t],
  )

  const showUnlockFields = parkingType === 'unlock' || parkingType === 'unlimited'
  const formType = toParkingFormType({ parkingType, isFree, externalTicket })
  const isUnlockQr = formType === 'unlock_qr'
  const isFreeFixed = formType === 'free_fixed'
  const showApplicationScope = formType === 'unlock' || formType === 'unlimited'
  const showBrandsFields = showApplicationScope && applicationScope === 'brands'
  const showTagsFields = showApplicationScope && applicationScope === 'tags'
  const showInvoiceUnlockFields = showUnlockFields && !isUnlockQr
  const showDurationInputs = parkingType !== 'unlimited' && !isFreeFixed
  const validityLocked = !isCreate && isFreeFixedValidityLocked(initialConfig)

  useEffect(() => {
    if (!tags.length) return
    const brandTagIds = new Set(tags.filter(tag => tag.group === 'BRANDS').map(tag => tag.id))
    setSelectedTags(prev => prev.filter(tag => brandTagIds.has(tag.value)))
  }, [tags])

  useEffect(() => {
    if (!tags.length) return
    const catalogById = new Map(tags.map(tag => [tag.id, tag]))
    const optionIds = new Set(tagCatalogOptions.map(option => option.value))
    setSelectedTags(prev => {
      const next = prev.filter(selected => {
        const tag = catalogById.get(selected.value)
        if (!tag) return false
        if (tag.active === false) return true
        return optionIds.has(selected.value)
      })
      return next.length === prev.length ? prev : next
    })
  }, [tagCatalogOptions, tags])

  useEffect(() => {
    if (!stores.length) return
    const labelsById = new Map(stores.map(store => [store.id, formatStoreLabel(store)]))
    setSelectedStores(prev => {
      let changed = false
      const next = prev.map(selected => {
        const label = labelsById.get(selected.value)
        if (!label || label === selected.label) return selected
        changed = true
        return { ...selected, label }
      })
      return changed ? next : prev
    })
  }, [stores])

  useEffect(() => {
    if (!stores.length) return
    const catalogById = new Map(stores.map(store => [store.id, store]))
    const optionIds = new Set(storeCatalogOptions.map(option => option.value))
    setSelectedStores(prev => {
      const next = prev.filter(selected => {
        const store = catalogById.get(selected.value)
        if (!store) return false
        if (store.active === false) return true
        return optionIds.has(selected.value)
      })
      return next.length === prev.length ? prev : next
    })
  }, [storeCatalogOptions, stores])

  useEffect(() => {
    if (memberships.length) {
      setLevelsState(buildInitialLevels(memberships, initialConfig))
    }
  }, [memberships, initialConfig])

  const presentationRules = useMemo(
    () => ({
      title: [
        {
          validate: (value: string) => StringUtils.extractTextFromHTML(value).length > 0,
          message: t('parking:validation.titleRequired') as string,
          validateEmpty: true,
        },
        {
          validate: (value: string) => StringUtils.extractTextFromHTML(value).length <= 70,
          message: t('parking:validation.titleMaxLength') as string,
        },
      ],
      shortTitle: [
        {
          validate: (value: string) => value.trim().length > 0,
          message: t('parking:validation.shortTitleRequired') as string,
          validateEmpty: true,
        },
        {
          validate: (value: string) => value.length <= 100,
          message: t('parking:validation.shortTitleMaxLength') as string,
        },
      ],
      additionalText: [
        {
          validate: (value: string) => value.length <= 30,
          message: t('parking:validation.additionalTextMaxLength') as string,
        },
      ],
      description: [
        {
          validate: (value: string) => value.trim().length > 0,
          message: t('parking:validation.descriptionRequired') as string,
          validateEmpty: true,
        },
        {
          validate: (value: string) => value.length <= 500,
          message: t('parking:validation.descriptionMaxLength') as string,
        },
      ],
      icon: [],
      image: [],
    }),
    [t],
  )

  const iconPreviewAvailable = !!iconPreview && !iconPreviewBroken
  const imagePreviewAvailable = !!imagePreview && !imagePreviewBroken

  const presentationFields = {
    title,
    shortTitle,
    additionalText,
    description,
    icon: icon || (iconPreviewAvailable ? iconPreview : null),
    image: image || (imagePreviewAvailable ? imagePreview : null),
  }

  const {
    hasErrors: presentationHasErrors,
    isAnyFieldEmpty: presentationHasEmptyFields,
    errors,
  } = useCheckFormErrors(presentationFields, presentationRules, [...PRESENTATION_REQUIRED_FIELDS])

  const includedLevels = Object.entries(levelsState).filter(([, level]) => level.included)

  const levelsValid =
    includedLevels.length > 0 &&
    includedLevels.every(([, level]) => {
      if (!showDurationInputs) return true
      return toTotalMinutes(level.hours, level.minutes) > 0
    })

  const invoiceFieldsValid = (() => {
    if (unlockAmount.trim() === '' || unlockMode === '') return false
    const parsed = Number(unlockAmount)
    return !Number.isNaN(parsed) && parsed >= 0
  })()
  const brandsSelectionValid = selectedStoreIds.length > 0
  const qrIdentityValid =
    !isUnlockQr ||
    (/^\d{1,4}$/.test(qrSystemId.trim()) &&
      /^\d{1,4}$/.test(qrIssuerId.trim()) &&
      /^\d$/.test(qrBenefitId.trim()))
  const tagsSelectionValid = selectedTagIds.length > 0
  const unlockFieldsValid =
    isUnlockQr ||
    !showUnlockFields ||
    (applicationScope === 'brands'
      ? brandsSelectionValid && invoiceFieldsValid
      : applicationScope === 'tags'
      ? tagsSelectionValid && invoiceFieldsValid
      : invoiceFieldsValid)

  const freeDatesValid =
    !isFreeFixed ||
    (!!validFrom && !!validTo && (toDateOnly(validTo) as string) >= (toDateOnly(validFrom) as string))
  const reglasValid = unlockFieldsValid && freeDatesValid && qrIdentityValid
  const presentacionValid = !presentationHasErrors && !presentationHasEmptyFields
  const alcanceValid = levelsValid

  const validFromHelperText = validityLocked
    ? (t('parking:hints.validityLocked') as string)
    : (t('parking:hints.validity') as string)
  const validToHelperText = validityLocked
    ? (t('parking:hints.validityLocked') as string)
    : (t('parking:hints.validity') as string)

  const isFormDisabled = isLoading || !reglasValid || !presentacionValid || !alcanceValid

  useEffect(() => {
    onValidityChange?.(isFormDisabled)
  }, [isFormDisabled, onValidityChange])

  const showTabError = (tabValid: boolean) => submitAttempted && !tabValid

  const titleCharCount = StringUtils.extractTextFromHTML(title).length

  const fieldError = (field: { hasError: boolean; message?: string }) =>
    submitAttempted && field.hasError

  const renderLengthHelper = (hint: string, current: number, max: number) => (
    <span className={classes.fieldHelperBlock}>
      <span className={classes.fieldHelperHint}>{hint}</span>
      <span className={classes.charCount}>{`${current}/${max}`}</span>
    </span>
  )

  const requiredMark = <span className={classes.requiredAsterisk}> *</span>

  const handleIconChange = async (file: File) => {
    setIconPreviewBroken(false)
    setIconPreview(URL.createObjectURL(file))
    const resizedIcon = await resizeFile(file, 192, 192)
    setIcon(resizedIcon)
  }

  const handleImageChange = async (file: File) => {
    setImagePreviewBroken(false)
    setImagePreview(URL.createObjectURL(file))
    const resizedImage = await resizeFile(file, 192, 192)
    setImage(resizedImage)
  }

  const handleIconDelete = () => {
    setIcon(null)
    setIconPreview('')
    setIconPreviewBroken(false)
  }

  const handleImageDelete = () => {
    setImage(null)
    setImagePreview('')
    setImagePreviewBroken(false)
  }

  const handleLevelRowToggle = (membershipId: number) => {
    if (isLoading) return
    setLevelsTouched(true)
    setLevelsState(prev => ({
      ...prev,
      [membershipId]: {
        ...prev[membershipId],
        included: !prev[membershipId].included,
      },
    }))
  }

  const handleLevelHoursChange = (membershipId: number) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const hours = event.target.value
    setLevelsTouched(true)
    setLevelsState(prev => ({
      ...prev,
      [membershipId]: {
        ...prev[membershipId],
        hours,
      },
    }))
  }

  const handleLevelMinutesChange = (membershipId: number) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const minutes = event.target.value
    setLevelsTouched(true)
    setLevelsState(prev => ({
      ...prev,
      [membershipId]: {
        ...prev[membershipId],
        minutes,
      },
    }))
  }

  const handleLevelDurationBlur = (membershipId: number, field: 'hours' | 'minutes') => () => {
    const max = field === 'hours' ? 23 : 59
    setLevelsState(prev => {
      const current = prev[membershipId]
      const normalized = String(parseDurationPart(current[field], max))
      if (normalized === current[field]) return prev
      return {
        ...prev,
        [membershipId]: {
          ...current,
          [field]: normalized,
        },
      }
    })
  }

  const handleApplicationScopeChange = (nextScope: ApplicationScope) => {
    setApplicationScope(nextScope)
    if (nextScope === 'brands') {
      setSelectedTags([])
      return
    }
    setSelectedStores([])
    if (nextScope !== 'tags') {
      setSelectedTags([])
    }
  }

  const handleFormTypeChange = (nextType: ParkingFormType) => {
    if (nextType === 'fixed' || nextType === 'free_fixed') {
      setParkingType('fixed')
      setIsFree(nextType === 'free_fixed')
      setExternalTicket(false)
      setApplicationScope('')
      setSelectedStores([])
      setSelectedTags([])
      if (nextType === 'fixed') {
        setValidFrom(null)
        setValidTo(null)
      }
      return
    }

    setIsFree(false)
    setValidFrom(null)
    setValidTo(null)

    if (nextType === 'unlock_qr') {
      setParkingType('unlock')
      setExternalTicket(true)
      setApplicationScope('')
      setSelectedStores([])
      setSelectedTags([])
      setUnlockAmount('')
      setUnlockMode('')
      return
    }

    setParkingType(nextType)
    setExternalTicket(false)
  }

  const jumpToFirstInvalidTab = () => {
    if (!presentacionValid) {
      setActiveTab(TAB_PRESENTACION)
      return
    }
    if (!reglasValid) {
      setActiveTab(TAB_REGLAS)
      return
    }
    if (!alcanceValid) {
      setActiveTab(TAB_ALCANCE)
    }
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitAttempted(true)
    setLevelsTouched(true)

    if (isFormDisabled) {
      snackbar.show(t('parking:validation.completeRequired') as string, 'error')
      jumpToFirstInvalidTab()
      return
    }

    const levels = includedLevels.map(([membershipId, level]) => ({
      membershipId: Number(membershipId),
      durationMinutes: showDurationInputs ? toTotalMinutes(level.hours, level.minutes) : null,
    }))

    onSubmit({
      parkingType,
      externalTicket: isUnlockQr,
      qrSystemId: isUnlockQr ? qrSystemId.trim() : null,
      qrIssuerId: isUnlockQr ? qrIssuerId.trim() : null,
      qrBenefitId: isUnlockQr ? Number(qrBenefitId.trim()) : null,
      unlockAmount: showInvoiceUnlockFields ? Number(unlockAmount) : null,
      unlockMode: showInvoiceUnlockFields ? (unlockMode as UnlockMode) : null,
      allTagsRequired: false,
      isFree: isFreeFixed,
      validFrom: isFreeFixed ? toDateOnly(validFrom) : null,
      validTo: isFreeFixed ? toDateOnly(validTo) : null,
      active,
      title: title.trim(),
      shortTitle: shortTitle.trim(),
      description: description.trim() || undefined,
      additionalText: additionalText.trim() || undefined,
      tagIds: showTagsFields ? selectedTagIds : [],
      storeIds: showBrandsFields ? selectedStoreIds : [],
      levels,
      icon,
      image,
    })
  }

  const tabLabel = (label: string, hasError: boolean) =>
    hasError ? (
      <span className={classes.tabLabelWithError}>
        {label}
        <span className={classes.tabErrorDot} />
      </span>
    ) : (
      label
    )

  return (
    <form id={formId} className={classes.form} onSubmit={handleSubmit} noValidate>
      <Tabs
        value={activeTab}
        onChange={(_, value: number) => setActiveTab(value)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
        variant="fullWidth"
      >
        <Tab label={tabLabel(t('parking:tabs.presentation'), showTabError(presentacionValid))} />
        <Tab label={tabLabel(t('parking:tabs.rules'), showTabError(reglasValid))} />
        <Tab label={tabLabel(t('parking:tabs.scope'), showTabError(alcanceValid))} />
      </Tabs>

      <div className={classes.tabPanel} hidden={activeTab !== TAB_PRESENTACION}>
        <Typography className={classes.sectionHint}>{t('parking:hints.presentation')}</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <EditorInput
              value={title}
              label={t('parking:fields.title') as string}
              readOnly={isLoading}
              required
              onChange={setTitle}
              maxLength={70}
              error={fieldError(errors.title)}
              helperText={renderLengthHelper(
                t('parking:hints.title') as string,
                titleCharCount,
                70,
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('parking:fields.shortTitle')}
              value={shortTitle}
              fullWidth
              onChange={event => setShortTitle(event.target.value)}
              disabled={isLoading}
              required
              error={fieldError(errors.shortTitle)}
              helperText={renderLengthHelper(
                t('parking:hints.shortTitle') as string,
                shortTitle.length,
                100,
              )}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('parking:fields.additionalText')}
              value={additionalText}
              fullWidth
              onChange={event => setAdditionalText(event.target.value)}
              disabled={isLoading}
              error={fieldError(errors.additionalText)}
              helperText={renderLengthHelper(
                t('parking:hints.additionalText') as string,
                additionalText.length,
                30,
              )}
              inputProps={{ maxLength: 30 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('parking:fields.description')}
              value={description}
              fullWidth
              onChange={event => setDescription(event.target.value)}
              disabled={isLoading}
              required
              multiline
              rows={4}
              error={fieldError(errors.description)}
              helperText={renderLengthHelper(
                t('parking:hints.description') as string,
                description.length,
                500,
              )}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>{t('parking:fields.images')}</Typography>
            <div className={classes.mediaRow}>
              <div className={classes.mediaSlot}>
                <ImageUploadField
                  id="parking-config-icon-upload-button"
                  label={
                    <>
                      {t('parking:fields.icon')}
                      {requiredMark}
                    </>
                  }
                  hint={t('parking:hints.icon') as string}
                  preview={iconPreviewAvailable ? iconPreview : undefined}
                  onChange={handleIconChange}
                  onDelete={handleIconDelete}
                  onPreviewError={() => setIconPreviewBroken(true)}
                  maxSizeInMB={2}
                  error={
                    submitAttempted && !presentationFields.icon
                      ? (t('parking:validation.iconRequired') as string)
                      : undefined
                  }
                />
              </div>
              <div className={classes.mediaSlot}>
                <ImageUploadField
                  id="parking-config-image-upload-button"
                  label={
                    <>
                      {t('parking:fields.image')}
                      {requiredMark}
                    </>
                  }
                  hint={t('parking:hints.image') as string}
                  variant="wide"
                  preview={imagePreviewAvailable ? imagePreview : undefined}
                  onChange={handleImageChange}
                  onDelete={handleImageDelete}
                  onPreviewError={() => setImagePreviewBroken(true)}
                  maxSizeInMB={2}
                  error={
                    submitAttempted && !presentationFields.image
                      ? (t('parking:validation.imageRequired') as string)
                      : undefined
                  }
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </div>

      <div className={classes.tabPanel} hidden={activeTab !== TAB_REGLAS}>
        <Typography className={classes.sectionHint}>{t('parking:hints.rules')}</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>{t('parking:fields.type')}</InputLabel>
              <Select
                value={formType}
                onChange={event => handleFormTypeChange(event.target.value as ParkingFormType)}
                disabled={isLoading || !isCreate}
              >
                {PARKING_FORM_TYPES.map(value => (
                  <MenuItem key={value} value={value}>
                    {t(`parking:types.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography className={classes.fieldHint}>
              {!isCreate ? t('parking:hints.typeLockedOnEdit') : t(`parking:typeHints.${formType}`)}
            </Typography>
          </Grid>

          {isUnlockQr && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  label={t('parking:fields.qrSystemId')}
                  value={qrSystemId}
                  onChange={event =>
                    setQrSystemId(event.target.value.replace(/\D/g, '').slice(0, 4))
                  }
                  error={submitAttempted && !/^\d{1,4}$/.test(qrSystemId.trim())}
                  helperText={t('parking:hints.qrSystemId') as string}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  label={t('parking:fields.qrIssuerId')}
                  value={qrIssuerId}
                  onChange={event =>
                    setQrIssuerId(event.target.value.replace(/\D/g, '').slice(0, 4))
                  }
                  error={submitAttempted && !/^\d{1,4}$/.test(qrIssuerId.trim())}
                  helperText={t('parking:hints.qrIssuerId') as string}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  label={t('parking:fields.qrBenefitId')}
                  value={qrBenefitId}
                  onChange={event =>
                    setQrBenefitId(event.target.value.replace(/\D/g, '').slice(0, 1))
                  }
                  error={submitAttempted && !/^\d$/.test(qrBenefitId.trim())}
                  helperText={t('parking:hints.qrBenefitId') as string}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography className={classes.fieldHint}>
                  {t('parking:hints.externalTicket') as string}
                </Typography>
              </Grid>
            </>
          )}

          {isFreeFixed && (
            <>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  value={validFrom}
                  onChange={date => setValidFrom(date as Moment | null)}
                  autoOk
                  format={STRINGS.DATE_FORMAT}
                  disabled={isLoading || validityLocked}
                  style={{ width: '100%' }}
                  label={t('parking:fields.validFrom')}
                  required
                  error={submitAttempted && !validFrom}
                  helperText={validFromHelperText}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  value={validTo}
                  onChange={date => setValidTo(date as Moment | null)}
                  autoOk
                  format={STRINGS.DATE_FORMAT}
                  disabled={isLoading || validityLocked}
                  minDate={validFrom || undefined}
                  style={{ width: '100%' }}
                  label={t('parking:fields.validTo')}
                  required
                  error={submitAttempted && (!validTo || !freeDatesValid)}
                  helperText={validToHelperText}
                />
              </Grid>
            </>
          )}

          {showApplicationScope && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('parking:fields.applicationScope')}</InputLabel>
                <Select
                  value={applicationScope}
                  displayEmpty
                  onChange={event =>
                    handleApplicationScopeChange(event.target.value as ApplicationScope)
                  }
                  disabled={isLoading}
                >
                  <MenuItem value="">{t('parking:scope.none')}</MenuItem>
                  <MenuItem value="brands">{t('parking:scope.brands')}</MenuItem>
                  <MenuItem value="tags">{t('parking:scope.tags')}</MenuItem>
                </Select>
                <FormHelperText>{t('parking:hints.applicationScope')}</FormHelperText>
              </FormControl>
            </Grid>
          )}

          {showBrandsFields && (
            <Grid item xs={12}>
              <InputLabel
                className={
                  submitAttempted && !brandsSelectionValid
                    ? `${classes.multiselectTitle} ${classes.multiselectTitleError}`
                    : classes.multiselectTitle
                }
              >
                {t('parking:fields.brands')}
                {requiredMark}
              </InputLabel>
              <div className={classes.selectionField}>
                {selectedStores.length > 0 && (
                  <div className={classes.selectionChips}>
                    {selectedStores.map(selected => {
                      const store = stores.find(item => item.id === selected.value)
                      const chipLabel = store ? formatStoreChipLabel(store) : selected.label
                      const fullLabel = store ? formatStoreLabel(store) : selected.label
                      const inactive = store ? store.active === false : false
                      const tooltip = inactive
                        ? `${fullLabel} (${t('parking:hints.inactiveSelection')})`
                        : fullLabel
                      return (
                        <Tooltip key={selected.value} title={tooltip} placement="top">
                          <Chip
                            label={chipLabel}
                            color={inactive ? 'default' : 'primary'}
                            size="small"
                            className={
                              inactive ? classes.selectionChipInactive : classes.selectionChip
                            }
                            onDelete={() =>
                              setSelectedStores(
                                selectedStores.filter(item => item.value !== selected.value),
                              )
                            }
                          />
                        </Tooltip>
                      )
                    })}
                  </div>
                )}
                <MultiSelect
                  options={brandOptions}
                  value={selectedStores}
                  onChange={setSelectedStores}
                  labelledBy={t('parking:upload.selectBrands') as string}
                  hasSelectAll={false}
                  ArrowRenderer={() => (
                    <ChevronDown className={classes.multiSelectArrow} size={24} />
                  )}
                  valueRenderer={() => (
                    <span className={classes.multiSelectPlaceholder}>
                      {t('parking:upload.addBrands')}
                    </span>
                  )}
                  className={classes.multiSelectChild}
                  overrideStrings={multiSelectStrings}
                />
              </div>
            </Grid>
          )}

          {showTagsFields && (
            <Grid item xs={12}>
              <InputLabel
                className={
                  submitAttempted && !tagsSelectionValid
                    ? `${classes.multiselectTitle} ${classes.multiselectTitleError}`
                    : classes.multiselectTitle
                }
              >
                {t('parking:fields.tags')}
                {requiredMark}
              </InputLabel>
              <div className={classes.selectionField}>
                {selectedTags.length > 0 && (
                  <div className={classes.selectionChips}>
                    {selectedTags.map(tag => {
                      const tagEntity = tags.find(item => item.id === tag.value)
                      const inactive = tagEntity ? tagEntity.active === false : false
                      const tooltip = inactive
                        ? `${tag.label} (${t('parking:hints.inactiveSelection')})`
                        : tag.label
                      return (
                        <Tooltip key={tag.value} title={tooltip} placement="top">
                          <Chip
                            label={tag.label}
                            color={inactive ? 'default' : 'primary'}
                            size="small"
                            className={
                              inactive ? classes.selectionChipInactive : classes.selectionChip
                            }
                            onDelete={() =>
                              setSelectedTags(selectedTags.filter(item => item.value !== tag.value))
                            }
                          />
                        </Tooltip>
                      )
                    })}
                  </div>
                )}
                <MultiSelect
                  options={tagCatalogOptions}
                  value={selectedTags}
                  onChange={setSelectedTags}
                  labelledBy={t('parking:upload.selectTags') as string}
                  hasSelectAll={false}
                  ArrowRenderer={() => <ChevronDown className={classes.multiSelectArrow} size={24} />}
                  valueRenderer={() => (
                    <span className={classes.multiSelectPlaceholder}>
                      {t('parking:upload.addTags')}
                    </span>
                  )}
                  className={classes.multiSelectChild}
                  overrideStrings={multiSelectStrings}
                />
              </div>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            sm={6}
            className={!showInvoiceUnlockFields ? classes.hiddenUnlockFields : undefined}
          >
            <TextField
              label={t('parking:fields.amount')}
              value={unlockAmount}
              fullWidth
              type="number"
              required={showInvoiceUnlockFields}
              onChange={event => setUnlockAmount(event.target.value)}
              disabled={isLoading || !showInvoiceUnlockFields}
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              error={
                showInvoiceUnlockFields &&
                submitAttempted &&
                !unlockFieldsValid &&
                unlockAmount.trim() === ''
              }
              helperText={
                showInvoiceUnlockFields ? (t('parking:hints.unlockAmount') as string) : undefined
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={!showInvoiceUnlockFields ? classes.hiddenUnlockFields : undefined}
          >
            <FormControl
              fullWidth
              required={showInvoiceUnlockFields}
              error={showInvoiceUnlockFields && submitAttempted && unlockMode === ''}
              disabled={isLoading || !showInvoiceUnlockFields}
            >
              <InputLabel>{t('parking:fields.mode')}</InputLabel>
              <Select
                value={unlockMode}
                onChange={event => setUnlockMode(event.target.value as UnlockMode)}
                disabled={isLoading || !showInvoiceUnlockFields}
              >
                {UNLOCK_MODES.map(value => (
                  <MenuItem key={value} value={value}>
                    {t(`parking:unlockModes.${value}`)}
                  </MenuItem>
                ))}
              </Select>
              {showInvoiceUnlockFields && (
                <FormHelperText>{t('parking:hints.unlockMode')}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </div>

      <div className={classes.tabPanel} hidden={activeTab !== TAB_ALCANCE}>
        <Typography className={classes.sectionHint}>{t('parking:hints.scope')}</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography
              className={
                (levelsTouched || submitAttempted) && !levelsValid
                  ? `${classes.sectionTitle} ${classes.sectionTitleError}`
                  : classes.sectionTitle
              }
            >
              {t('parking:fields.levels')}
              {requiredMark}
            </Typography>
            <Typography className={classes.fieldHint}>
              {showDurationInputs
                ? t('parking:hints.levelsWithMinutes')
                : t('parking:hints.levelsUnlimited')}
            </Typography>
            <div className={classes.levelsList}>
              {memberships.map(membership => {
                const level = levelsState[membership.id]
                if (!level) return null

                return (
                  <div
                    key={membership.id}
                    role="button"
                    tabIndex={isLoading ? -1 : 0}
                    className={`${classes.levelRow}${
                      level.included ? ` ${classes.levelRowActive}` : ''
                    }`}
                    onClick={() => handleLevelRowToggle(membership.id)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleLevelRowToggle(membership.id)
                      }
                    }}
                  >
                    <FormControlLabel
                      className={classes.levelCheckbox}
                      control={
                        <Checkbox checked={level.included} disabled={isLoading} tabIndex={-1} />
                      }
                      label={membership.title}
                    />
                    {showDurationInputs && (
                      <div
                        className={`${classes.levelDuration}${
                          level.included
                            ? ` ${classes.levelMinutesInteractive}`
                            : ` ${classes.levelMinutesHidden}`
                        }`}
                        onClick={event => event.stopPropagation()}
                        onKeyDown={event => event.stopPropagation()}
                      >
                        <TextField
                          className={classes.levelDurationUnit}
                          label={t('parking:fields.hours')}
                          value={level.hours}
                          onChange={handleLevelHoursChange(membership.id)}
                          onBlur={handleLevelDurationBlur(membership.id, 'hours')}
                          disabled={isLoading || !level.included}
                          type="number"
                          size="small"
                          inputProps={{
                            min: 0,
                            max: 23,
                            step: 1,
                            tabIndex: level.included ? 0 : -1,
                          }}
                        />
                        <TextField
                          className={classes.levelDurationUnit}
                          label={t('parking:fields.minutes')}
                          value={level.minutes}
                          onChange={handleLevelMinutesChange(membership.id)}
                          onBlur={handleLevelDurationBlur(membership.id, 'minutes')}
                          disabled={isLoading || !level.included}
                          type="number"
                          size="small"
                          inputProps={{
                            min: 0,
                            max: 59,
                            step: 1,
                            tabIndex: level.included ? 0 : -1,
                          }}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Grid>
        </Grid>
      </div>

      <button type="submit" hidden disabled={isLoading} />
    </form>
  )
}

export { ParkingConfigForm }
